import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { TaskAggregateKey, TaskListFilters, TaskSort } from "@/domain/task";
import { getTaskRepository } from "@/infrastructure/di";
import { queryKeys } from "../queryKeys";
import { distinctTaskPages } from "./cache";

export const usePendingItemList = (
  orgId: string,
  filters: TaskListFilters,
  sort: readonly TaskSort[],
  pageSize: number,
) =>
  useInfiniteQuery({
    queryKey: queryKeys.tasks.list(orgId, { filters, sort }, { pageSize }),
    queryFn: ({ pageParam, signal }) =>
      getTaskRepository().listPendingItems(
        { ...filters, sort, limit: pageSize, offset: pageParam },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.items.length, 0);
      return loaded < lastPage.total_count ? loaded : undefined;
    },
    select: distinctTaskPages,
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });

export const usePendingItemAggregates = (
  orgId: string,
  filters: TaskListFilters,
  aggregates: readonly TaskAggregateKey[],
) =>
  useQuery({
    queryKey: queryKeys.tasks.aggregates(orgId, { ...filters }, aggregates),
    queryFn: ({ signal }) =>
      getTaskRepository().getPendingAggregates({ ...filters, aggregates }, signal),
    enabled: !!orgId && aggregates.length > 0,
    placeholderData: keepPreviousData,
  });

export const usePendingSummary = (orgId: string) =>
  useQuery({
    queryKey: queryKeys.tasks.summary(orgId),
    queryFn: ({ signal }) => getTaskRepository().getPendingSummary(signal),
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });

export const downloadPendingItemsExport = (
  filters: TaskListFilters,
  sort: readonly TaskSort[],
  options: { format: "csv" | "xlsx" | "json"; columns: string[] },
) => getTaskRepository().downloadExport({ ...filters, sort }, options);
